'use client';

import { NextIntlClientProvider, IntlErrorCode, type IntlError } from 'next-intl';
import type { ComponentProps, ReactNode } from 'react';

type Messages = ComponentProps<typeof NextIntlClientProvider>['messages'];
type ProviderLocale = ComponentProps<typeof NextIntlClientProvider>['locale'];

/**
 * Wrapper du provider next-intl CÔTÉ CLIENT avec filet anti-crash.
 *
 * Une clé i18n manquante (clé ajoutée non encore rechargée par le dev server, edge prod,
 * faute de frappe) NE DOIT JAMAIS faire tomber l'UI : incident — la chip « Réflexion » de la
 * timeline agentique a levé `MISSING_MESSAGE` et crashé tout le bloc de réflexion pendant un
 * run. On log et on dégrade vers le dernier segment de la clé.
 *
 * `onError`/`getMessageFallback` sont des FONCTIONS : elles ne traversent pas la frontière
 * RSC → impossible de les passer au provider depuis un Server Component (le layout). D'où ce
 * wrapper client. Le provider hérite quand même `messages`/`locale`/`formats` du contexte serveur.
 */
function onError(error: IntlError) {
  if (error.code === IntlErrorCode.MISSING_MESSAGE) {
    console.warn(`[i18n] ${error.message}`);
    return;
  }
  throw error;
}

function getMessageFallback({ key }: { key: string }): string {
  return key.split('.').pop() ?? key;
}

export function IntlProvider({
  locale,
  messages,
  timeZone,
  children,
}: {
  locale: ProviderLocale;
  messages: Messages;
  timeZone: string;
  children: ReactNode;
}) {
  // `locale`/`messages`/`timeZone` passés EXPLICITEMENT : un wrapper client ne peut plus
  // hériter du contexte serveur (sinon « Couldn't infer the `locale` prop » côté locale, et
  // `ENVIRONMENT_FALLBACK` côté timeZone dès qu'un composant formate une date).
  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      timeZone={timeZone}
      onError={onError}
      getMessageFallback={getMessageFallback}
    >
      {children}
    </NextIntlClientProvider>
  );
}
